"use client";

/**
 * ScreenBeacon (spec 2026-09-18, R-L1): the app tells the server the screen it
 * is showing, so a task that finishes, fails or asks while its person is
 * LOOKING AT IT says nothing. Mounted ONCE by AppShell, beside LineActions, so
 * it is on every app route, the task page included. Renders nothing.
 *
 * The server keeps the frame ON THE SOCKET, and it dies with the socket. So:
 *   - a CHANGE of view or of the task on screen is sent once, 300 ms after the
 *     last change, keyed so an unchanged screen sends nothing (the rule and the
 *     key are useLine's own: lib/composer/screen-context);
 *   - LEAVING a task's screen is sent AT ONCE. The one silence is keyed on the
 *     task the server believes is on screen, so a debounced departure would keep
 *     a task that rests in that blink as "shown" and tell it on no rung. An
 *     arrival may wait: late, it only costs a sentence they did not need;
 *   - a VISIBILITY change is sent AT ONCE. A hidden tab is a connected socket,
 *     not a person looking, and a browser throttles a hidden tab's timers, so a
 *     debounce here could leave the server believing someone is watching;
 *   - every (re)CONNECT sends the current screen again, whatever the key says:
 *     the new socket knows nothing, and a socket that said nothing reads as
 *     "not watching", which only ever costs a sentence they did not need.
 *
 * Unflagged and additive: an older server has no handler and ignores it.
 */
import { useCallback, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { EVENTS, type ComposerScreenPayload } from "@bash/shared";
import { contextKey, contextOf } from "@/lib/composer/screen-context";
import { useConnectionStore } from "@/stores/connectionStore";
import { useTaskStore } from "@/stores/taskStore";

export const SCREEN_DEBOUNCE_MS = 300;

const tabVisible = (): boolean =>
  typeof document === "undefined" || document.visibilityState === "visible";

export function ScreenBeacon(): null {
  const pathname = usePathname();
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const isSocketConnected = useConnectionStore((s) => s.isSocketConnected);
  /** The screen the listeners read, so none of them closes over a stale render. */
  const screen = useRef({ pathname: pathname ?? "", selectedTaskId });
  /** What THIS socket has been told; "" = nothing yet. */
  const told = useRef("");
  /** The task THIS socket believes is on screen, so a departure can be told at once. */
  const toldTask = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasConnected = useRef(false);

  const send = useCallback((): void => {
    const socket = useConnectionStore.getState().getSocket();
    // A socket that is down is told nothing (socket.io would buffer a stale frame); the connect edge below tells it the truth.
    if (!socket?.connected) return;
    const context = contextOf(screen.current.pathname, screen.current.selectedTaskId);
    const visible = tabVisible();
    const key = `${contextKey(context)}|${visible ? 1 : 0}`;
    if (key === told.current) return;
    told.current = key;
    toldTask.current = context.activeTaskId ?? null;
    const payload: ComposerScreenPayload = { ...context, visible };
    socket.emit(EVENTS.COMPOSER_SCREEN, payload);
  }, []);

  // A change of view or of the task on screen: once, this long after the last.
  useEffect(() => {
    screen.current = { pathname: pathname ?? "", selectedTaskId };
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    // Leaving the task the server believes is on screen: at once, never debounced.
    const next = contextOf(screen.current.pathname, selectedTaskId).activeTaskId ?? null;
    if (toldTask.current && next !== toldTask.current) {
      send();
      return;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      send();
    }, SCREEN_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [pathname, selectedTaskId, send]);

  // Visibility: at once, in both directions.
  useEffect(() => {
    const onVisibility = (): void => send();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [send]);

  // The connect EDGE (the mount on a live socket is one): the new socket is told, whatever the key says.
  useEffect(() => {
    const connected = isSocketConnected && !wasConnected.current;
    wasConnected.current = isSocketConnected;
    if (!connected) return;
    told.current = "";
    toldTask.current = null;
    send();
  }, [isSocketConnected, send]);

  return null;
}
